export const requiredAircraftDocumentTypes = [
  'POH',
  'ARC',
  'Insurance',
];

export function getAircraftDocumentStatus(
  documents = []
) {
  const documentRows =
    requiredAircraftDocumentTypes.map(
      (documentType) => {
        const document =
          documents.find(
            (documentRow) =>
              normalizeDocumentType(
                documentRow?.document_type
              ) ===
              normalizeDocumentType(documentType)
          ) || null;

        const expiryMeta = getExpiryMeta(
          document?.expiry_date
        );

        return {
          documentType,
          document,
          expiryMeta,
        };
      }
    );

  const missing = documentRows.filter(
    (row) => !row.document
  );

  const expired = documentRows.filter(
    (row) =>
      row.document &&
      row.expiryMeta.status === 'expired'
  );

  const warnings = documentRows.filter(
    (row) =>
      row.document &&
      row.expiryMeta.status === 'warning'
  );

  const notices = documentRows.filter(
    (row) =>
      row.document &&
      row.expiryMeta.status === 'notice'
  );

  let overall = 'current';

  if (missing.length > 0 || expired.length > 0) {
    overall = 'problem';
  } else if (
    warnings.length > 0 ||
    notices.length > 0
  ) {
    overall = 'warning';
  }

  const messages = documentRows
    .map((row) => {
      if (!row.document) {
        return {
          key: row.documentType,
          level: 'problem',
          message: `${getShortDocumentTypeLabel(
            row.documentType
          )} is not uploaded.`,
        };
      }

      if (
        row.expiryMeta.status === 'expired' ||
        row.expiryMeta.status === 'warning' ||
        row.expiryMeta.status === 'notice'
      ) {
        return {
          key: row.documentType,
          level:
            row.expiryMeta.status === 'notice'
              ? 'warning'
              : 'problem',
          message: `${getShortDocumentTypeLabel(
            row.documentType
          )}: ${row.expiryMeta.label}.`,
        };
      }

      return null;
    })
    .filter(Boolean);

  return {
    overall,
    documentRows,
    missing,
    expired,
    warnings,
    notices,
    messages,
    allPresent: missing.length === 0,
    allCurrent:
      missing.length === 0 &&
      expired.length === 0 &&
      warnings.length === 0 &&
      notices.length === 0,
  };
}

export function getDocumentTypeLabel(
  documentType
) {
  const labels = {
    POH: '📘 Pilot Operating Handbook',
    'Weight & Balance':
      '⚖️ Weight & Balance',
    ARC: '📋 Airworthiness Review Certificate',
    Insurance: '🛡️ Insurance',
  };

  return (
    labels[documentType] ||
    `📄 ${documentType || 'Document'}`
  );
}

export function getShortDocumentTypeLabel(
  documentType
) {
  const labels = {
    POH: 'Pilot Operating Handbook',
    'Weight & Balance': 'Weight & Balance',
    ARC: 'Airworthiness Review Certificate',
    Insurance: 'Insurance',
  };

  return (
    labels[documentType] ||
    documentType ||
    'Document'
  );
}

export function getExpiryMeta(expiryDate) {
  if (!expiryDate) {
    return {
      label: 'No expiry date',
      colour: '#8db7e6',
      status: 'none',
      daysRemaining: null,
    };
  }

  const expiry = new Date(
    `${expiryDate}T23:59:59`
  );

  if (!Number.isFinite(expiry.getTime())) {
    return {
      label: 'Expiry date unavailable',
      colour: '#8db7e6',
      status: 'unknown',
      daysRemaining: null,
    };
  }

  const today = new Date();

  const daysRemaining = Math.ceil(
    (expiry.getTime() - today.getTime()) /
      (24 * 60 * 60 * 1000)
  );

  const formattedDate =
    expiry.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  if (daysRemaining < 0) {
    return {
      label: `Expired ${formattedDate}`,
      colour: '#ff8d8d',
      status: 'expired',
      daysRemaining,
    };
  }

  if (daysRemaining <= 30) {
    return {
      label: `Expires ${formattedDate} · ${daysRemaining} days`,
      colour: '#ff8d8d',
      status: 'warning',
      daysRemaining,
    };
  }

  if (daysRemaining <= 90) {
    return {
      label: `Expires ${formattedDate} · ${daysRemaining} days`,
      colour: '#ffd27a',
      status: 'notice',
      daysRemaining,
    };
  }

  return {
    label: `Expires ${formattedDate}`,
    colour: '#8ff4b6',
    status: 'current',
    daysRemaining,
  };
}

function normalizeDocumentType(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}